import type { Options as RehypeSanitizeOptions } from 'rehype-sanitize';
import rehypeSanitize from 'rehype-sanitize';
import rehypeRaw from 'rehype-raw';
import type { MarkdownPreviewProps } from '@uiw/react-markdown-preview';
import { rehypePrism } from './rehype-prism';
import { rehypeWrapTables } from './rehype-wrap-tables';

export type RehypeNode = {
  type: string;
  tagName?: string;
  properties?: Record<string, unknown>;
  children?: RehypeNode[];
  value?: string;
};

/**
 * Custom rehype plugin to fix ordered list numbering continuity.
 *
 * When markdown contains ordered lists broken by images or code blocks,
 * parsers create separate <ol> elements. This plugin detects consecutive
 * ordered lists and sets the correct `start` attribute to maintain numbering.
 *
 * Example markdown that breaks:
 * 1. Step one
 * ![image](url)
 * 2. Step two
 *
 * Without this plugin, both lists start at 1. With this plugin, the second
 * list gets start="2" to continue numbering correctly.
 */
export function rehypeFixOrderedListContinuity() {
  return (tree: RehypeNode) => {
    if (!tree.children || !Array.isArray(tree.children)) return;

    let lastOlIndex: number | null = null;
    let lastOlEndNumber = 0;

    function isNeutralNode(node: RehypeNode): boolean {
      if (node.type === 'text' && (!node.value || node.value.trim() === '')) {
        return true;
      }

      if (node.type === 'element') {
        if (node.tagName === 'img' || node.tagName === 'figure') {
          return true;
        }

        if (node.tagName === 'p' && node.children) {
          const hasOnlyImages = node.children.every(
            (child) =>
              (child.type === 'element' && child.tagName === 'img') ||
              (child.type === 'text' &&
                (!child.value || child.value.trim() === '')),
          );

          if (hasOnlyImages && node.children.some((c) => c.tagName === 'img')) {
            return true;
          }
        }

        if (node.tagName === 'pre') {
          return true;
        }
      }

      return false;
    }

    function countListItems(olNode: RehypeNode): number {
      if (!olNode.children) return 0;

      return olNode.children.filter(
        (child) => child.type === 'element' && child.tagName === 'li',
      ).length;
    }

    function getOlStart(olNode: RehypeNode): number {
      const start = olNode.properties?.start;

      if (typeof start === 'number') return start;
      if (typeof start === 'string') return parseInt(start, 10) || 1;

      return 1;
    }

    for (let i = 0; i < tree.children.length; i++) {
      const node = tree.children[i];

      if (node.type === 'element' && node.tagName === 'ol') {
        if (lastOlIndex !== null) {
          let allNeutral = true;

          for (let j = lastOlIndex + 1; j < i; j++) {
            if (!isNeutralNode(tree.children[j])) {
              allNeutral = false;
              break;
            }
          }

          if (allNeutral) {
            const currentStart = getOlStart(node);

            if (currentStart === 1) {
              node.properties = node.properties || {};
              node.properties.start = lastOlEndNumber + 1;
            }
          }
        }

        const itemCount = countListItems(node);
        const startNum = getOlStart(node);

        lastOlEndNumber = startNum + itemCount - 1;
        lastOlIndex = i;
      } else if (!isNeutralNode(node)) {
        lastOlIndex = null;
        lastOlEndNumber = 0;
      }
    }

    function processNodeList(nodes: RehypeNode[]): void {
      if (!nodes || !Array.isArray(nodes)) return;

      let localLastOlIndex: number | null = null;
      let localLastOlEndNumber = 0;

      for (let i = 0; i < nodes.length; i++) {
        const node = nodes[i];

        if (node.type === 'element' && node.tagName === 'ol') {
          if (localLastOlIndex !== null) {
            let allNeutral = true;

            for (let j = localLastOlIndex + 1; j < i; j++) {
              if (!isNeutralNode(nodes[j])) {
                allNeutral = false;
                break;
              }
            }

            if (allNeutral) {
              const currentStart = getOlStart(node);

              if (currentStart === 1) {
                node.properties = node.properties || {};
                node.properties.start = localLastOlEndNumber + 1;
              }
            }
          }

          const itemCount = countListItems(node);
          const startNum = getOlStart(node);

          localLastOlEndNumber = startNum + itemCount - 1;
          localLastOlIndex = i;
        } else if (!isNeutralNode(node)) {
          localLastOlIndex = null;
          localLastOlEndNumber = 0;
        }
      }
    }

    function recurse(node: RehypeNode): void {
      if (node.type === 'element' && node.children) {
        if (
          node.tagName !== 'ol' &&
          node.tagName !== 'ul' &&
          node.tagName !== 'li'
        ) {
          processNodeList(node.children);
        }

        for (const child of node.children) {
          recurse(child);
        }
      }
    }

    for (const child of tree.children) {
      recurse(child);
    }
  };
}

/**
 * Custom rehype plugin to escape anchor links that are not markdown header links.
 * Markdown header links like [text](#header) are preserved, while external links
 * are converted to styled text showing "LinkText (URL)" format.
 */
export function rehypeEscapeAnchorLinks() {
  return (tree: RehypeNode) => {
    function walk(node: RehypeNode, parent?: RehypeNode, index?: number): void {
      if (!node || typeof node !== 'object') return;

      if (node.type === 'element' && node.tagName === 'a') {
        const href = node.properties?.href;
        const hrefString = href ? String(href) : '';

        if (!hrefString.startsWith('#')) {
          const linkText =
            (node.children || [])
              .map((child) => {
                if (child.type === 'text') return child.value || '';

                return '';
              })
              .join('') || '';

          if (parent && typeof index === 'number' && parent.children) {
            const urlText = hrefString || linkText;
            const hasLinkText = linkText && linkText !== hrefString;

            if (hasLinkText) {
              parent.children[index] = {
                type: 'element',
                tagName: 'span',
                properties: {
                  className: ['escaped-link-container'],
                },
                children: [
                  {
                    type: 'text',
                    value: linkText,
                  },
                  {
                    type: 'text',
                    value: ' (',
                  },
                  {
                    type: 'element',
                    tagName: 'span',
                    properties: {
                      className: ['escaped-link'],
                      'data-url': urlText,
                    },
                    children: [
                      {
                        type: 'text',
                        value: urlText,
                      },
                    ],
                  },
                  {
                    type: 'text',
                    value: ')',
                  },
                ],
              };
            } else {
              parent.children[index] = {
                type: 'element',
                tagName: 'span',
                properties: {
                  className: ['escaped-link'],
                  'data-url': urlText,
                },
                children: [
                  {
                    type: 'text',
                    value: urlText,
                  },
                ],
              };
            }
          }
        } else {
          if (Array.isArray(node.children)) {
            node.children.forEach((child, idx) => {
              walk(child, node, idx);
            });
          }
        }
      } else if (Array.isArray(node.children)) {
        node.children.forEach((child, idx) => {
          walk(child, node, idx);
        });
      }
    }

    if (tree.children && Array.isArray(tree.children)) {
      tree.children.forEach((child, idx) => {
        walk(child, tree, idx);
      });
    }
  };
}

/**
 * Custom rehype plugin to escape unsafe HTML tags instead of stripping them.
 */
export function rehypeEscapeUnsafeTags() {
  return (tree: RehypeNode) => {
    const unsafeTags = [
      'script',
      'iframe',
      'object',
      'embed',
      'form',
      'input',
      'button',
      'img',
      'style',
      'link',
      'meta',
      'base',
      'frame',
      'frameset',
      'applet',
      'marquee',
      'blink',
    ];

    function escapeElementToHtml(node: RehypeNode): string {
      if (!node.tagName) return '';

      const tagName = node.tagName;
      const attributes = node.properties
        ? Object.entries(node.properties)
            .map(([key, value]) => {
              if (value === true) return ` ${key}`;
              if (value === false || value === null || value === undefined)
                return '';

              return ` ${key}="${String(value).replace(/"/g, '&quot;')}"`;
            })
            .join('')
        : '';

      const children = (node.children || [])
        .map((child) => {
          if (child.type === 'text') return child.value || '';
          if (child.type === 'element') {
            return escapeElementToHtml(child);
          }

          return '';
        })
        .join('');

      return `<${tagName}${attributes}>${children}</${tagName}>`;
    }

    function walk(node: RehypeNode, parent?: RehypeNode, index?: number): void {
      if (!node || typeof node !== 'object') return;

      if (node.type === 'element' && typeof node.tagName === 'string') {
        if (unsafeTags.includes(node.tagName)) {
          const escapedHtml = escapeElementToHtml(node);

          if (parent && typeof index === 'number' && parent.children) {
            parent.children[index] = {
              type: 'element',
              tagName: 'span',
              properties: { className: ['escaped-html'] },
              children: [
                {
                  type: 'text',
                  value: escapedHtml,
                },
              ],
            };
          }
        } else {
          if (Array.isArray(node.children)) {
            node.children.forEach((child, idx) => {
              walk(child, node, idx);
            });
          }
        }
      } else if (Array.isArray(node.children)) {
        node.children.forEach((child, idx) => {
          walk(child, node, idx);
        });
      }
    }

    if (tree.children && Array.isArray(tree.children)) {
      tree.children.forEach((child, idx) => {
        walk(child, tree, idx);
      });
    }
  };
}

export const sanitizeConfig: RehypeSanitizeOptions = {
  tagNames: [
    'p',
    'br',
    'strong',
    'em',
    'u',
    's',
    'del',
    'ins',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'ul',
    'ol',
    'li',
    'blockquote',
    'code',
    'pre',
    'table',
    'thead',
    'tbody',
    'tr',
    'th',
    'td',
    'hr',
    'span',
    'div',
    'a',
  ],

  attributes: {
    '*': ['className', 'data*'],
    a: ['href', 'title'],
    ol: ['start'],
  },
};

/**
 * Create the ordered list of rehype plugins.
 * Order matters: escape links/tags first, then fix lists, then sanitize.
 */
export function createRehypePlugins(): MarkdownPreviewProps['rehypePlugins'] {
  return [
    rehypeRaw,
    rehypeEscapeAnchorLinks,
    rehypeEscapeUnsafeTags,
    rehypeFixOrderedListContinuity,
    rehypeWrapTables,
    [rehypePrism, { ignoreMissing: true }],
    [rehypeSanitize, sanitizeConfig],
  ];
}
